/**
 * 저장된 뷰 — 필터 조합에 이름을 붙여 한 번에 부른다.
 *
 * **도구줄의 원칙을 그대로 따른다: 안 정한 것은 안 그린다.** 저장한 것이
 * 없고 걸린 필터도 없으면 이 자리는 통째로 사라진다 — 그래야 아무것도 안
 * 건 보드의 도구줄이 지금처럼 한 줄로 남는다.
 *
 * 담기는 곳은 브라우저다(`savedViewStore.ts` 머리말 — 왜 표가 아닌지).
 */

import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';

import type { TicketFilter } from '../api';
import {
  addView,
  hasFilter,
  loadViews,
  NAME_MAX,
  removeView,
  sameFilter,
  type SavedView,
  storeViews,
} from './savedViewStore';

export function SavedViews({
  projectId,
  filter,
  onFilter,
}: {
  projectId: number | null;
  filter: TicketFilter;
  onFilter: (next: TicketFilter) => void;
}) {
  // ⚠ **첫 값을 effect 로 채우지 않는다.** 그러면 한 번은 빈 목록으로
  // 그려졌다가 다시 그려지고, 「저장한 것이 없다」가 한순간 거짓말이 된다.
  // 프로젝트가 바뀌는 것은 **다른 자료를 보는 것**이라 부르는 쪽이
  // `key` 로 마디를 새로 세운다 — 상태를 effect 로 되감는 것보다 싸고,
  // 옛 프로젝트의 뷰가 남아 남의 것으로 읽히는 길이 아예 없다.
  const [views, setViews] = useState<SavedView[]>(() =>
    projectId === null ? [] : loadViews(projectId),
  );
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (naming) inputRef.current?.focus();
  }, [naming]);

  if (projectId === null) return null;

  const canSave = hasFilter(filter);
  if (views.length === 0 && !canSave) return null;

  const commit = (next: SavedView[]) => {
    setViews(next);
    storeViews(projectId, next);
  };

  const save = () => {
    const next = addView(views, name, filter);
    // 빈 이름이면 `null` 이다 — 그때는 아무 일도 안 하고 칸만 닫는다.
    if (next) commit(next);
    setName('');
    setNaming(false);
  };

  return (
    <>
      {views.map((view) => {
        const on = sameFilter(view.filter, filter);
        return (
          <span
            key={view.name}
            className="inline-flex min-h-s6 items-center rounded-md border border-line bg-sunk pl-s3 pr-[1px] text-meta max-[720px]:min-h-[44px]"
          >
            <button
              type="button"
              onClick={() => onFilter(view.filter)}
              aria-pressed={on}
              // 지금 보고 있는 뷰를 굵기가 아니라 **색**으로 말한다 —
              // 맑은고딕에 500·600 이 없어 굵기 차이가 창마다 달리 보인다.
              className={on ? 'py-[2px] pr-s2 text-brand' : 'py-[2px] pr-s2 text-ink-2'}
            >
              {view.name}
            </button>
            <button
              type="button"
              onClick={() => commit(removeView(views, view.name))}
              aria-label={`저장된 뷰 「${view.name}」 지우기`}
              className="grid size-s5 shrink-0 place-items-center rounded-sm text-ink-3 transition-colors hover:bg-panel hover:text-ink max-[720px]:size-s8"
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
                <path
                  d="M2.5 2.5l6 6M8.5 2.5l-6 6"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </span>
        );
      })}

      {/* 저장은 **걸린 필터가 있을 때만**이다. 없으면 저장할 것이 없고,
          단추만 서 있으면 무엇이 담기는지 알 수 없다. */}
      {canSave && !naming && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="max-[720px]:h-[44px]"
          onClick={() => setNaming(true)}
        >
          이 필터 저장
        </Button>
      )}

      {canSave && naming && (
        <span className="inline-flex items-center gap-s2">
          <input
            ref={inputRef}
            value={name}
            maxLength={NAME_MAX}
            onChange={(e) => setName(e.target.value)}
            // ⚠ **조합 중(IME)인 Enter 를 삼키지 마라** — 한글 입력에서
            // 첫 Enter 는 글자를 확정하는 것이지 저장이 아니다.
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing) return;
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') {
                setName('');
                setNaming(false);
              }
            }}
            aria-label="저장할 뷰 이름"
            placeholder="이름"
            className="h-s7 w-[9rem] rounded-md border border-line bg-panel px-s3 text-meta text-ink placeholder:text-ink-3 max-[720px]:h-[44px]"
          />
          <Button
            type="button"
            size="sm"
            className="max-[720px]:h-[44px]"
            onClick={save}
          >
            저장
          </Button>
        </span>
      )}
    </>
  );
}
